import { StyleSheet, Text, View, Image } from "react-native";
import React, { useEffect, useState, useCallback, useRef, memo } from "react";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { useNavigation } from "@react-navigation/native";
import { AppColors } from "../../../../utility/AppColors";
import { AppImages } from "../../../../utility/AppImages";
import { CommonButton } from "../../../../common/CommonButton";
import { ScrollView } from "react-native-gesture-handler";
import InviteOpponentModal from "../../Event/InviteOpponentModal";
import InviteSocialMediaModal from "../../Event/InviteSocialMediaModal";
import SearchFromFriendsModal from "../../Event/SearchFromFriendsModal";
import PlayersView from "../../../../Components/ongoing-common-components/participant-components/PlayersView";
import Skeletons from "../../../../Components/skeleton-components/Skeletons";
import OpponentActivity from "../../../../Components/ongoing-common-components/participant-components/OpponentActivity";
import SignedUpViewers from "../../../../Components/ongoing-common-components/participant-components/SignedUpViewers";
import { toast } from "react-toastify";
import { handleApiError } from "../../../../services/errorHandler";
import {
  deleteNotification,
  getNotificationFromEvent,
} from "../../../../services/eventsAPI";
import WinnerModal from "../Modals/WinnerModal";
import { useNotifications } from "../../../../context/NotificationContext";
import { base_url, endpoints } from "../../../../Api/configs";
import CornfirmPurchaseBeltModal from "../BeltScreens/CornfirmPurchaseBeltModal";
import BeltPurchaseSuccessModal from "../BeltScreens/BeltPurchaseSuccessModal";
import { navigateToScreen } from "../../../../utility/NavigationHelper";
import { AppScreens } from "../../../Utility/AppScreens";
import { parseResponseBody } from "../../../../services/apiClient";

// Import optimized types
import { EventDetails, UserData, NotificationData } from "./types/event.types";
import { safeAsync } from "./utils/eventUtils";
import { storeUserActivity, ACTIVITY_TYPES } from "../../../../services/activityService";

interface ActivityItem {
  username: string;
  profilePicture?: string;
  activity: string;
  time: string;
}

interface OnGoingGameShowParticipantProps {
  showLoader?: boolean;
  details: EventDetails | null;
  userData: UserData | null;
  isOrganiserId?: boolean;
  eventID: string;
  accessToken: string;
  setDetails: (value?: any) => void;
  IsEventCanceled: boolean;
  IsEventCompleted?: boolean;
  arrBeltMedalType?: any[];
  setArrBeltMedalType?: (belts: any[]) => void;
  userBalanceData?: any;
  activities?: ActivityItem[];
}

const OnGoingGameShowParticipant = memo<OnGoingGameShowParticipantProps>(({
  showLoader,
  details,
  accessToken,
  userData,
  isOrganiserId = false,
  eventID,
  setDetails,
  IsEventCanceled,
  IsEventCompleted,
  arrBeltMedalType = [],
  setArrBeltMedalType,
  userBalanceData,
  activities = [],
}) => {
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const navigation = useNavigation();
  const isMountedRef = useRef(true);

  // Notification context
  const { eventStatus, updateNotification, sendNotification, setUpdateNotification } = useNotifications();

  // State management with proper types
  const [challengeDetails, setChallengeDetails] = useState<any>(null);
  const [notificationData, setNotificationData] = useState<NotificationData>({} as NotificationData);
  const [notfoundMessage, setNotfoundMessage] = useState<string>("");
  const [inviteOpponentModal, setInviteOpponentModal] = useState<boolean>(false);
  const [searchFromFriendsModal, setSearchFromFriendsModal] = useState<boolean>(false);
  const [inviteSocialMediaModal, setInviteSocialMediaModal] = useState<boolean>(false);
  const [showAPILoader, setAPILoader] = useState<boolean>(false);
  const [showWinnerModal, setShowWinnerModal] = useState<boolean>(false);
  const [cornfirmPurchaseBeltModal, setCornfirmPurchaseBeltModal] = useState<boolean>(false);
  const [beltPurchaseSuccessModal, setBeltPurchaseSuccessModal] = useState<boolean>(false);
  const [insufficientStakesError, setInsufficientStakesError] = useState<any>(null);
  const [numberOfBelts, setNumberOfBelts] = useState<string>("0");
  const [mergedViewers, setMergedViewers] = useState<any[]>([]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Memoized challenge details data
  const challengeDetailsData = React.useMemo(() => {
    if (!details) return {};

    return {
      ChallengerID: details.organiserId,
      GameName: details.gameTitle,
      Type: "Internal",
      Message: "Good luck!",
      EventID: details.eventID,
      organiserId: details.organiserId,
      StakeType: details.stakes?.[0]?.type || '',
      StakeAmount: details.stakes?.[0]?.quantity || 0,
      DateTime: details.eventStartTime,
    };
  }, [details]);

  // Optimized winner modal logic
  useEffect(() => {
    if (!details?.eventEndTime || !details?.eventStartTime) {
      return;
    }

    const currentTime = new Date().getTime();
    const eventEndTime = new Date(details.eventEndTime).getTime();
    const eventHasEnded = currentTime > eventEndTime;

    // Show winner modal only if event has ended and there's a valid result
    if (eventHasEnded && details.winner) {
      setShowWinnerModal(true);
    } else {
      setShowWinnerModal(false);
    }
  }, [details?.eventEndTime, details?.eventStartTime, details?.winner]);

  // Optimized API functions with proper error handling
  const getNotificationFromEventAPI = useCallback(async () => {
    if (!accessToken || !eventID || !isMountedRef.current) {
      return;
    }

    setAPILoader(true);

    try {
      const response = await safeAsync(() =>
        getNotificationFromEvent(accessToken, eventID)
      );

      if (!isMountedRef.current) return;

      if (response?.statusCode === 200) {
        if (response.body?.notification) {
          setNotificationData(response.body.notification);
        }
        if (response.body?.status === "not_found") {
          setNotfoundMessage("Challenge not sent");
        }
      } else if (response?.statusCode === 403) {
        const responseBody = response.body || {};
        if (responseBody.message === "You are not the organizer of this event") {
          console.warn("You are not authorized to view notifications for this event.");
        } else {
          handleApiError(response.statusCode, responseBody.error || "Forbidden access");
        }
      } else if (response?.statusCode) {
        handleApiError(
          response.statusCode,
          response.body?.error || "An unexpected error occurred"
        );
      }
    } catch (error: any) {
      if (isMountedRef.current) {
        toast.error(error.message || "Failed to fetch notification data");
      }
    } finally {
      if (isMountedRef.current) {
        setAPILoader(false);
      }
    }
  }, [accessToken, eventID]);

  // Load notification data on mount and when dependencies change
  useEffect(() => {
    if (accessToken && eventID) {
      getNotificationFromEventAPI();
    }
  }, [getNotificationFromEventAPI]);

  // Listen for challenge status updates (when opponent accepts/rejects challenge)
  useEffect(() => {
    if (eventStatus && eventStatus.eventId === eventID) {
      console.log("=== OnGoingEventParticipant: Challenge status update received ===", {
        eventId: eventStatus.eventId,
        status: eventStatus.status,
        challengeDetails: eventStatus.challengeDetails,
      });

      // Refresh event details and notification data to show updated participant info
      console.log("Refreshing participant section due to challenge status update...");
      setDetails();
      getNotificationFromEventAPI();

    }
  }, [eventStatus, eventID, setDetails, getNotificationFromEventAPI]);

  // Listen for organizer selecting new opponent (via updateNotification)
  useEffect(() => {
    if (
      updateNotification &&
      typeof updateNotification === "object" &&
      "challengeId" in updateNotification &&
      updateNotification.challengeId === eventID
    ) {
      console.log("=== OnGoingEventParticipant: Opponent change notification received ===", {
        challengeId: updateNotification.challengeId,
        response: updateNotification.response,
      });

      // Refresh event details and notification data to show new opponent
      console.log("Refreshing participant section due to opponent change...");
      setDetails();
      getNotificationFromEventAPI();

    }
  }, [updateNotification, eventID, setDetails, getNotificationFromEventAPI]);

  const deleteNotificationAPI = useCallback(async () => {
    if (!accessToken || !eventID) return;

    setAPILoader(true);

    try {
      const response = await safeAsync(() => deleteNotification(accessToken, eventID));

      if (!isMountedRef.current || !response) return;

      const statusCode = response.statusCode;
      const responseBody = response.body;

      switch (statusCode) {
        case 200:
          toast.success(responseBody.message || "Notification successfully deleted.");
          if (isOrganiserId) {
            setDetails();
          }
          setInviteOpponentModal(true);
          break;

        case 404:
          toast.info(responseBody.message || "No rejected notification found for this event.");
          if (isOrganiserId) {
            setDetails();
          }
          setInviteOpponentModal(true);
          break;

        case 403:
          toast.warning(responseBody.message || "You are not authorized to perform this action.");
          break;

        case 409:
          toast.info(responseBody.message || "The challenge is pending. Wait for an accept/reject.");
          break;

        case 500:
          toast.error(responseBody.message || "Something went wrong, please try again later.");
          break;

        default:
          toast.error("Unexpected error occurred.");
      }
    } catch (error: any) {
      if (isMountedRef.current) {
        toast.error(error.message || "Failed to process request.");
      }
    } finally {
      if (isMountedRef.current) {
        setAPILoader(false);
      }
    }
  }, [accessToken, eventID, isOrganiserId, setDetails]);

  // Handle accept/reject challenge
  const handleChallengeResponse = useCallback(async (userResponse: boolean) => {
    if (!accessToken || !notificationData?.notificationId) {
      toast.error("Missing required information to respond to challenge");
      return;
    }

    setAPILoader(true);
    const url = `${base_url}${endpoints.events.challengeRespond}`;

    try {
      const response = await fetch(url, {
        mode: "cors",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accessToken,
          notificationId: notificationData.notificationId,
          response: userResponse,
        }),
      });

      let data = await response.json();
      data = parseResponseBody(data);

      if (response.status === 200) {
        if (data.statusCode === 200 || data.statusCode === 201) {
          toast.success(
            userResponse
              ? "Challenge Accepted Successfully"
              : "Challenge Rejected Successfully"
          );

          const { body } = data;
          const { notificationId, senderId } = body;

          const updateResponse = {
            challengeId: eventID,
            response: userResponse,
          };

          setUpdateNotification(updateResponse);
          await sendNotification(senderId, notificationId);

          // Refresh event details and notification data
          setDetails();
          getNotificationFromEventAPI();

          // Track activity (fire and forget)
          storeUserActivity(
            accessToken,
            eventID,
            userResponse ? ACTIVITY_TYPES.CHALLENGE_ACCEPTED : ACTIVITY_TYPES.CHALLENGE_REJECTED
          ).catch((err) => console.warn("Failed to track activity:", err));
        } else if (data.statusCode === 400) {
          // Handle insufficient stakes error by showing belt purchase modal
          let errorMessage = "";
          if (data.body && typeof data.body === "object" && data.body.error) {
            errorMessage = data.body.error;
          } else if (typeof data.body === "string") {
            errorMessage = data.body;
          }

          // Extract required belts from error message
          const match = errorMessage.match(/Required:\s*(\d+)\s*(\w+)/i);
          if (match) {
            const requiredAmount = parseInt(match[1]);
            const beltType = match[2];

            setInsufficientStakesError({
              requiredAmount,
              beltType,
              fullMessage: errorMessage,
            });

            // Calculate how many belts the user needs to buy
            const userBronzeBelts = arrBeltMedalType.find((b) => b.title === "Bronze")?.value || 0;
            const beltsToBuy = Math.max(0, requiredAmount - userBronzeBelts);
            setNumberOfBelts(beltsToBuy.toString());

            setCornfirmPurchaseBeltModal(true);
          } else {
            // If we can't parse the error, show it as toast
            toast.error(errorMessage || "Insufficient belts to accept this challenge");
          }
        } else {
          try {
            const parsedData = JSON.parse(data.body);
            toast.error(parsedData.message || "An error occurred");
          } catch (error) {
            toast.error(data.body);
          }
        }
      } else {
        toast.error("Failed to respond to challenge");
      }
    } catch (error: any) {
      if (isMountedRef.current) {
        toast.error(error.message || "Failed to respond to challenge");
      }
    } finally {
      if (isMountedRef.current) {
        setAPILoader(false);
      }
    }
  }, [accessToken, notificationData, eventID, setDetails, getNotificationFromEventAPI, sendNotification, setUpdateNotification]);

  // Prepare merged viewers (participants + attendees)
  React.useEffect(() => {
    if (details) {
      const participants = details.participantDetails || [];
      const attendees = details.attendeeIDs || [];

      // Combine and deduplicate
      const merged = [...participants];
      attendees.forEach((attendeeId: string) => {
        if (!participants.find((p) => p.userID === attendeeId)) {
          merged.push({ userID: attendeeId, profilePicture: undefined });
        }
      });

      setMergedViewers(merged);
    }
  }, [details]);



  const isMobileView = deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

  return (
    <ScrollView style={styles.container} showsHorizontalScrollIndicator={false}>
      {isMobileView ? (
        // Mobile Layout: Single column
        <View style={{ gap: 20, marginBottom: 80 }}>
          <PlayersView
            styles={styles}
            details={details}
            userData={userData}
            showLoader={showLoader}
            notificationData={notificationData}
            notfoundMessage={notfoundMessage}
            isOrganiserId={isOrganiserId}
            showAPILoader={showAPILoader}
            onSelectOpponent={deleteNotificationAPI}
            onAcceptChallenge={() => handleChallengeResponse(true)}
            onRejectChallenge={() => handleChallengeResponse(false)}
            IsEventCanceled={IsEventCanceled}
            IsEventCompleted={IsEventCompleted}
          />

          <OpponentActivity activities={activities} isLoading={showLoader} isMobile={true} />

          <SignedUpViewers
            viewers={mergedViewers}
            totalCount={mergedViewers.length}
            isLoading={showLoader}
            navigation={navigation}
          />
        </View>
      ) : (
        // Desktop Layout: Left column (VS + Signed Up Viewers), Right column (Opponent Activity)
        <View style={{ marginTop: 25, flexDirection: "row", gap: 20 }}>
          {/* Left Column - 70% - VS Section + Signed Up Viewers */}
          <View style={{ flex: 7 }}>
            <PlayersView
              styles={styles}
              details={details}
              userData={userData}
              showLoader={showLoader}
              notificationData={notificationData}
              notfoundMessage={notfoundMessage}
              isOrganiserId={isOrganiserId}
              showAPILoader={showAPILoader}
              onSelectOpponent={deleteNotificationAPI}
              onAcceptChallenge={() => handleChallengeResponse(true)}
              onRejectChallenge={() => handleChallengeResponse(false)}
              IsEventCanceled={IsEventCanceled}
              IsEventCompleted={IsEventCompleted}
            />

            <View style={{ marginTop: 30 }}>
              <SignedUpViewers
                viewers={mergedViewers}
                totalCount={mergedViewers.length}
                isLoading={showLoader}
                navigation={navigation}
              />
            </View>
          </View>

          {/* Right Column - 30% - Opponent Activity (Full Height) */}
          <View style={{ flex: 3 }}>
            <OpponentActivity activities={activities} isLoading={showLoader} />
          </View>
        </View>
      )}
      {inviteOpponentModal && (
        <InviteOpponentModal
          showModal={inviteOpponentModal}
          setShowModal={setInviteOpponentModal}
          challengeDetails={challengeDetails}
          selectedOption={(options) => {
            setInviteOpponentModal(false);
            if (options === "Friends") {
              setSearchFromFriendsModal(true);
            } else {
              setInviteSocialMediaModal(true);
            }
          }}
        />
      )}
      {searchFromFriendsModal && (
        <SearchFromFriendsModal
          accessToken={accessToken}
          challengeDetails={challengeDetailsData}
          showModal={searchFromFriendsModal}
          setShowModal={setSearchFromFriendsModal}
          successCallback={(response) => {
            console.log("Success callback send friends", response);
            if (response.challengeDetails) {
              // Refresh event details to get updated participant list
              setDetails();
            }
          }}
        />
      )}
      {inviteSocialMediaModal && (
        <InviteSocialMediaModal
          accessToken={accessToken}
          challengeDetails={{
            type: "External",
            EventID: details.eventID,
            organiserId: details.organiserId,
          }}
          showModal={inviteSocialMediaModal}
          setShowModal={setInviteSocialMediaModal}
          eventType={"OnGoingGameShowParticipant"}
          eventID={details.eventID}
          status={""}
        />
      )}
      {showWinnerModal && (
        <WinnerModal
          closeWinnerModal={() => setShowWinnerModal(false)}
          openWinnerModal={showWinnerModal}
          eventDetails={details}
          winnerDetails={details}
        />
      )}

      {/* Belt Purchase Confirmation Modal */}
      {cornfirmPurchaseBeltModal && (
        <CornfirmPurchaseBeltModal
          accessToken={accessToken}
          numberOfBelts={numberOfBelts}
          setArrBeltMedalType={setArrBeltMedalType}
          arrBeltMedalType={arrBeltMedalType}
          showModal={cornfirmPurchaseBeltModal}
          insufficientStakesError={insufficientStakesError}
          setShowModal={setCornfirmPurchaseBeltModal}
          eventID={eventID}
          selectedBelt={() => {
            setBeltPurchaseSuccessModal(true);
          }}
          userBalanceData={userBalanceData}
          jumpToPurchaseToken={(isNavigate) => {
            if (isNavigate) {
              // Store the state that we need to return to belt modal
              localStorage.setItem('returnToBeltModal', JSON.stringify({
                eventID,
                insufficientStakesError,
                numberOfBelts,
              }));
              navigateToScreen(navigation, AppScreens.PurchaseScreen, {
                eventID,
                type: "ongoing",
              });
            }
          }}
          setRefreshAPICall={() => {
            setDetails();
          }}
        />
      )}

      {/* Belt Purchase Success Modal */}
      {beltPurchaseSuccessModal && (
        <BeltPurchaseSuccessModal
          showModal={beltPurchaseSuccessModal}
          setShowModal={setBeltPurchaseSuccessModal}
          arrBeltMedalType={arrBeltMedalType}
          numberOfBelts={numberOfBelts}
          backProfileTapped={() => {
            setBeltPurchaseSuccessModal(false);
            setDetails();
          }}
          dataBuyBelt={insufficientStakesError}
        />
      )}
    </ScrollView>
  );
});

OnGoingGameShowParticipant.displayName = 'OnGoingGameShowParticipant';

export default OnGoingGameShowParticipant;

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
    },
    btnStyle: {
      paddingVertical: 0,
      paddingHorizontal: 30,
      backgroundColor: "transparent",
      shadowOpacity: 0,
      height: 40,
      borderWidth: 1,
      borderColor: AppColors.white,
      borderRadius: 10,
      margin: 10,
      top: 30,
      alignSelf: "center",
      right: "-15%",
    },
    playersView: {
      justifyContent: "center",
      flexDirection: "row",
      gap: 50,
    },
    playerView: {
      //   margin: 5,
      backgroundColor: AppColors.grayColor_282828,
      borderRadius: 12,
      // paddingHorizontal: 10,
    },
    imgstyle: {
      height: 200,
      width: 175,
      margin: 10,
    },
    VsImg: {
      height: 300,
      width: 250,
      marginVertical: 10,
      marginTop: 50,
      alignSelf: "center",
      resizeMode: "contain",
    },
    deviceflexDirection: {
      flexDirection: "row",
    },
  },
  {
    [DEVICE_SIZES.MD]: {
      container: {
        flex: 1,
      },
      btnStyle: {
        right: "3%",
        left: 0,
      },
      playerView: {
        // margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      imgstyle: {
        height: 150,
        width: 120,
        borderWidth: 1,
        borderColor: "black",
        margin: 10,
      },
      VsImg: {
        height: 200,
        width: 120,
        marginVertical: 10,
        alignSelf: "center",
        resizeMode: "contain",
      },
    },
    [DEVICE_SIZES.LG]: {
      container: {
        flex: 1,
      },
      playersView: {
        flexDirection: "row",
        gap: 30,
      },
      playerView: {
        // margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },

      deviceflexDirection: {
        flexDirection: "row",
      },
      imgstyle: {
        height: 200,
        width: 175,
        margin: 10,
      },
      btnStyle: {
        right: "3%",
        left: 85,
      },
      VsImg: {
        height: 250,
        width: 150,
        resizeMode: "contain",
      },
    },

    [DEVICE_SIZES.SM]: {
      container: {
        flex: 1,
      },
      playersView: {
        flexDirection: "row",
        gap: 8,
        justifyContent: "center",
        paddingHorizontal: 10,
      },
      playerView: {
        // margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 8,
        maxWidth: 165,
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      imgstyle: {
        height: 160,
        width: 150,
        marginTop: 10,
        marginBottom: 10,
        margin: 0,
      },
      btnStyle: {
        top: 30,
        right: 0,
        width: 170,
      },
      VsImg: {
        height: 180,
        width: 150,
        resizeMode: "contain",
      },
    },
    [DEVICE_SIZES.XS]: {
      container: {
        flex: 1,
      },
      playersView: {
        flexDirection: "row",
        gap: 5,
        justifyContent: "center",
        paddingHorizontal: 8,
      },
      playerView: {
        // margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 6,
        maxWidth: 110,
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      imgstyle: {
        height: 95,
        width: 78,
        marginTop: 10,
        marginBottom: 10,
        margin: 0,
      },
      VsImg: {
        height: 150,
        width: 120,
        marginTop: 50,
        alignSelf: "center",
        resizeMode: "contain",
      },
      btnStyle: {
        left: 0,
        width: "auto",
      },
    },
  }
);
